import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Sentry } from '@org/utils-client';
import { AudioSettingsPopover, useAudioSettings } from '../../../custom/audio-settings';
import { OnAirCallView } from './on-air-call-view';
import { useOpenAIRealtime, type ConversationEndOutcome } from '../contents/use-openai-realtime';
import type { CompletionResult, ConversationState } from '../contents/conversational-exercise-types';

export type { CompletionResult, ConversationEndOutcome };

export interface PracticeCallSession {
  sessionId: string;
  ephemeralKey: string;
  /** Length granted by the server (already shortened to today's remaining time). */
  durationSec: number;
}

export interface PracticeCallProps {
  session: PracticeCallSession;
  /** Fires once, for every way the call can end (End button, timer, inactivity, the AI, lost connection, leaving the page). */
  onEnded: (outcome: ConversationEndOutcome) => void;
  /** Fires when connecting to the voice provider fails before the call starts. */
  onStartError?: (error: Error) => void;
  /** MellinAI's photo for the host circle (the app passes its own asset). */
  hostImageSrc?: string;
  /** The student's two initials for the guest circle. */
  guestInitials?: string;
  guestName?: string;
  /** Small eyebrow with the topic, under the ON AIR row. */
  topicTitle?: string;
}

/**
 * The live call of a free practice conversation with MellinAI, shown as a
 * podcast studio "ON AIR" (`OnAirCallView`). Same push-to-talk engine as the
 * lesson exercises (`useOpenAIRealtime`): this component only owns the
 * connection lifecycle for a session that the
 * `conversationPractice.startSession` procedure already created.
 */
export function PracticeCall({
  session,
  onEnded,
  onStartError,
  hostImageSrc,
  guestInitials,
  guestName,
  topicTitle,
}: PracticeCallProps) {
  const { t } = useTranslation('p2e');
  const [conversationState, setConversationState] = useState<ConversationState>('connecting');
  const endedRef = useRef(false);

  const {
    selectedMicId,
    setSelectedMicId,
    selectedSpeakerId,
    setSelectedSpeakerId,
    getMicConstraints,
    setSpeakerOnElement,
  } = useAudioSettings();

  const openai = useOpenAIRealtime({
    maxDurationSec: session.durationSec,
    getMicConstraints,
    setSpeakerOnElement,
    onEnding: () => setConversationState('ending'),
    onEnded: (outcome) => {
      if (endedRef.current) return;
      endedRef.current = true;
      setConversationState('completed');
      onEnded(outcome);
    },
  });

  // Connect once for this session; leaving the page finishes the session on
  // the server (evaluated from what was saved) instead of leaving it hanging.
  // The ephemeral key is single-use, so the connect is deferred one tick: React
  // StrictMode (dev) mounts → unmounts → remounts at once, and the first mount's
  // cleanup cancels the timer before any request is made. Without this the
  // remount reused a spent key and OpenAI answered 401.
  useEffect(() => {
    let cancelled = false;
    let started = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      started = true;
      void connectOnce();
    }, 0);
    const connectOnce = async () => {
      try {
        await openai.connect({ sessionId: session.sessionId, ephemeralKey: session.ephemeralKey });
        if (!cancelled) setConversationState('active');
      } catch (err) {
        if (cancelled) return;
        const isMicError =
          err instanceof DOMException &&
          ['NotAllowedError', 'NotFoundError', 'NotReadableError'].includes(err.name);
        Sentry.captureException(err, {
          level: isMicError ? 'warning' : 'error',
          tags: { feature: 'mellinai-practice', stage: 'start' },
          extra: { sessionId: session.sessionId },
        });
        openai.cleanup();
        onStartError?.(
          err instanceof Error
            ? isMicError
              ? new Error(t('mellinai.call.micError'))
              : err
            : new Error(t('mellinai.call.startError')),
        );
      }
    };
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (!started) return;
      if (openai.isActive()) {
        void openai.endConversation('unmount').catch(() => undefined);
      } else {
        openai.cleanup();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.sessionId]);

  // The caption shows MellinAI's latest finished sentence.
  const hostLine = useMemo(() => {
    for (let i = openai.turns.length - 1; i >= 0; i -= 1) {
      const turn = openai.turns[i];
      if (turn.role === 'assistant' && !turn.pending && !turn.failed && turn.content) return turn.content;
    }
    return '';
  }, [openai.turns]);

  if (conversationState === 'connecting') {
    return (
      <div
        className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-center"
        style={{ background: '#FFF8F5' }}
      >
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#D41920' }} />
        <p className="m-0 text-sm" style={{ color: '#756560' }}>
          {t('mellinai.call.connecting')}
        </p>
      </div>
    );
  }

  return (
    <OnAirCallView
      hostLine={hostLine}
      aiLiveTranscript={openai.aiLiveTranscript}
      isRecording={openai.isRecording}
      isAiSpeaking={openai.isAiSpeaking}
      isAiThinking={openai.isAiThinking}
      currentTranscript={openai.currentTranscript}
      timeRemaining={openai.timeRemaining}
      idleSecondsLeft={openai.idleSecondsLeft}
      quietSeconds={openai.quietSeconds}
      isWrappingUp={openai.isWrappingUp}
      conversationState={conversationState}
      error={openai.error}
      getLevels={openai.getLevels}
      hostImageSrc={hostImageSrc}
      guestInitials={guestInitials}
      guestName={guestName}
      topicTitle={topicTitle}
      settingsSlot={
        <AudioSettingsPopover
          selectedMicId={selectedMicId}
          onMicSelect={setSelectedMicId}
          selectedSpeakerId={selectedSpeakerId}
          onSpeakerSelect={setSelectedSpeakerId}
        />
      }
      onEndConversation={() => {
        void openai.endConversation('user').catch(() => undefined);
      }}
      onToggleRecording={openai.handleToggleRecording}
      onPointerDown={openai.handlePointerDown}
      onPointerUp={openai.handlePointerUp}
    />
  );
}
